import React, { useState, useEffect } from "react";
import Tesseract from "tesseract.js";
import Header from "./Header";
import { toast } from "react-toastify";

const TexttoSpeech = () => {
  const [text, setText] = useState("");
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [utterance, setUtterance] = useState(null); // Track the utterance
  const [isSpeaking, setIsSpeaking] = useState(false); // Track speaking state
  const [isPaused, setIsPaused] = useState(false); // Track paused state

  useEffect(() => {
    const handleVoicesChanged = () => {
      const availableVoices = speechSynthesis.getVoices();
      const bangladeshiVoice = availableVoices.find(
        (voice) => voice.lang === "bn-BD"
      );
      setSelectedVoice(bangladeshiVoice); // Set Bangladeshi voice by default
    };

    speechSynthesis.onvoiceschanged = handleVoicesChanged;
    handleVoicesChanged(); // Initial load

    return () => {
      speechSynthesis.onvoiceschanged = null; // Clean up listener
    };
  }, []);

  const handleTextChange = (event) => {
    setText(event.target.value);
  };

  const toggleSpeak = () => {
    if (isSpeaking) {
      stopSpeaking();
    } else {
      speak();
    }
  };

  const speak = () => {
    if (text.trim() === "") {
      toast.warn("Please enter some text or upload an image!");
      return;
    }

    const newUtterance = new SpeechSynthesisUtterance(text);
    newUtterance.voice = selectedVoice;

    newUtterance.onend = () => {
      console.log("Speech has finished.");
      setIsSpeaking(false); // Update speaking state
      setUtterance(null); // Clear utterance when finished
      setIsPaused(false); // Reset paused state
    };

    newUtterance.onerror = (event) => {
      console.error("Speech synthesis error:", event.error);
    };

    speechSynthesis.speak(newUtterance);
    setUtterance(newUtterance); // Track the current utterance
    setIsSpeaking(true); // Update speaking state
  };

  const stopSpeaking = () => {
    if (utterance) {
      speechSynthesis.cancel(); // Stop the speech synthesis
      setIsSpeaking(false); // Update speaking state
      setUtterance(null); // Clear the utterance
      setIsPaused(false); // Reset paused state
    }
  };

  const pauseSpeaking = () => {
    if (isSpeaking && !isPaused) {
      speechSynthesis.pause(); // Pause the speech
      setIsPaused(true); // Update paused state
    }
  };

  const resumeSpeaking = () => {
    if (isSpeaking && isPaused) {
      speechSynthesis.resume(); // Resume the speech
      setIsPaused(false); // Update paused state
    }
  };

  const [loading, setloading] = useState(false);

  const handleImageUpload = (event) => {
    setloading(true);
    const file = event.target.files[0];
    if (file) {
      extractTextFromImage(file);
    }
  };

  const extractTextFromImage = (file) => {
    Tesseract.recognize(file, "eng", {
      logger: (m) => console.log(m), // Optional: log progress
    })
      .then(({ data: { text } }) => {
        setText(text);
        setloading(false);
        console.log("Extracted text:", text);
      })
      .catch((err) => {
        console.error("Error extracting text:", err);
      });
  };

  return (
    <div className="pb-4">
      <Header />

      <h1 className="text-center">Text to Speech</h1>
      <div className="text-center text-secondary fw-semibold">
        Convert text to the most natural sounding voice online for free
      </div>

      <div className="d-none d-lg-block d-md-block mt-4 border border-white rounded-3 p-3 w-50 m-auto">
        <div>
          <div className="border rounded-3 position-relative">
            <textarea
              value={text}
              onChange={handleTextChange}
              rows="10"
              cols="30"
              placeholder="Enter text here..."
              className="form-control bg-transparent"
              style={{ outline: "none" }}
            />
            {loading && (
              <div className="position-absolute start-50 top-50 translate-middle">
                <span className="loader"></span>
              </div>
            )}
          </div>
          <br />

          <input
            type="file"
            className="form-control w-50"
            accept="image/*"
            onChange={handleImageUpload}
          />
          <br />

          <div className="d-flex justify-content-end">
            <button
              className={`btn ${isSpeaking ? "btn-danger" : "btn-info"}`}
              onClick={toggleSpeak}
            >
              {isSpeaking ? "Stop" : "Generate Speech"}
              <span
                className={`ms-2 bi ${isSpeaking ? "bi-stop" : "bi-megaphone"}`}
              ></span>
            </button>
            {isSpeaking && (
              <>
                <button
                  className="btn btn-warning ms-2"
                  onClick={pauseSpeaking}
                  disabled={isPaused}
                >
                  Pause
                </button>
                <button
                  className="btn btn-success ms-2"
                  onClick={resumeSpeaking}
                  disabled={!isPaused}
                >
                  Resume
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="d-sm-block d-lg-none d-md-none mt-4 border border-white rounded-3 p-3 w-100 m-auto">
        <div className="border rounded-3">
          <textarea
            value={text}
            onChange={handleTextChange}
            rows="20"
            cols="30"
            placeholder="Enter text here..."
            className="form-control bg-transparent"
            style={{ outline: "none" }}
          />
          {loading && (
            <div className="position-absolute top-50 start-50 translate-middle">
              <span className="loader"></span>
            </div>
          )}
        </div>
        <br />

        <input
          type="file"
          className="form-control w-100"
          accept="image/*"
          onChange={handleImageUpload}
        />
        <br />

        <div className="d-flex justify-content-end">
          <button
            className={`btn ${isSpeaking ? "btn-danger" : "btn-info"}`}
            onClick={toggleSpeak}
          >
            {isSpeaking ? "Stop" : "Generate Speech"}
            <span
              className={`ms-2 bi ${isSpeaking ? "bi-stop" : "bi-megaphone"}`}
            ></span>
          </button>
          {isSpeaking && (
            <>
              <button
                className="btn btn-warning ms-2"
                onClick={pauseSpeaking}
                disabled={isPaused}
              >
                Pause
              </button>
              <button
                className="btn btn-success ms-2"
                onClick={resumeSpeaking}
                disabled={!isPaused}
              >
                Resume
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TexttoSpeech;
